import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Donut } from '@p81-common/ui';

import {
  Area,
  LineHS,
  TextChart,
  AreaBig
} from 'components/common/charts/charts';
import { Loading, PageSubTitle } from 'components/common/Elements/Elements';

import { postRequest } from 'utils/requests';
import { underlineToSpace, moneyFormaterFull } from 'utils/common';
import useWindowSize from 'hooks/useWindowSize';
import { useRegion } from 'contexts/RegionContext';

import classes from './General.module.scss';
import { colors } from './colors';

const General = ({ customerId, status }) => {
  const { region } = useRegion();
  const [generalData, setGeneralData] = useState({});
  const [healthScoreData, setHealthScoreData] = useState({});
  const [activeUsersData, setActiveUsersData] = useState({});
  const [loaderGeneral, setLoaderGeneral] = useState(true);
  const [loaderUsers, setLoaderUsers] = useState(true);
  const [show, setShow] = useState(true);
  const [oldSize, setOldSize] = useState(0);

  const size = useWindowSize();
  const { t } = useTranslation(['activity']);

  const { url } = useRouteMatch();

  let newUrl = url.split('/');
  newUrl.pop();
  newUrl = newUrl.join('/');

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);
    postRequest('/customer/general', {
      customerId: customerId
    })
      .then(res => {
        if (!cancelled) {
          setGeneralData(res.body || {});
          setLoaderGeneral(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.log(err);
          setLoaderGeneral(false);
        }
      });
    return () => (cancelled = true);
  }, [customerId, region.code]);

  useEffect(() => {
    let cancelled = false;
    postRequest('/customer/general/activeusers', {
      customerId: customerId
    })
      .then(res => {
        if (!cancelled) {
          setActiveUsersData(res.body || {});
          setLoaderUsers(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.log(err);
          setLoaderUsers(false);
        }
      });
    return () => (cancelled = true);
  }, [customerId, region.code]);

  useEffect(() => {
    let cancelled = false;
    postRequest('/customer/general/healthscore', {
      customerId: customerId
    })
      .then(res => {
        !cancelled && setHealthScoreData(res.body || {});
      })
      .catch(err => {
        !cancelled && console.log(err);
      });
    return () => (cancelled = true);
  }, [customerId, region.code]);

  useEffect(() => {
    let timer = null;
    if (oldSize > size.width || (size.width >= 1441 && oldSize < 1441)) {
      setShow(false);
      setOldSize(size.width);
      timer = setTimeout(() => setShow(true), 500);
    } else setOldSize(size.width);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [size.width]);

  const memberUtilizationData = {
    title: `${generalData.tmUtilized}/${generalData.tmLicenses}`,
    subtitle: t('activity:customers.UTILIZIZED_MEMBERS'),
    values: { current: generalData.tmUtilized, max: generalData.tmLicenses },
    data: [
      {
        name: 'One',
        value: generalData.tmUtilized,
        color: colors.licenses.membersFull
      },
      {
        name: 'Empty',
        value: generalData.tmLicenses - generalData.tmUtilized,
        color: colors.licenses.membersEmpty
      }
    ]
  };

  const gatewaysUtilizationData = {
    title: `${generalData.psUtilized}/${generalData.psLicenses}`,
    subtitle: t('activity:customers.UTILIZIZED_GATEWAYS'),
    values: { current: generalData.psUtilized, max: generalData.psLicenses },
    data: [
      {
        name: 'One',
        value: generalData.psUtilized,
        color: colors.licenses.gatewaysFull
      },
      {
        name: 'Empty',
        value: generalData.psLicenses - generalData.psUtilized,
        color: colors.licenses.gatewaysEmpty
      }
    ]
  };

  const mAppsUtilizationData = {
    title: `${generalData.appsUtilized}/${generalData.appsLicenses}`,
    subtitle: t('activity:customers.UTILIZIZED_APPS'),
    values: {
      current: generalData.appsUtilized,
      max: generalData.appsLicenses
    },
    data: [
      {
        name: 'One',
        value: generalData.appsUtilized,
        color: colors.licenses.appsFull
      },
      {
        name: 'Empty',
        value: generalData.appsLicenses - generalData.appsUtilized,
        color: colors.licenses.appsEmpty
      }
    ]
  };

  const billingCicle = {
    nextDate: status !== 'Non Renewing' ? generalData.nextBillingDate : 'NA',
    contentText: generalData.billingCycle
      ? underlineToSpace(generalData.billingCycle)
      : 'NA',
    title: t('activity:customers.BILLING_CYCLE'),
    message: t('activity:customers.NEXT_BILLING_DATE'),
    warning: generalData.isDueToBeCancelled ? true : false
  };

  const loyalty = {
    nextDate: generalData.firstPaymentDate,
    contentText: generalData.ageInMonths ? `${generalData.ageInMonths} mo.` : 0,
    title: t('activity:customers.AGE_IN_MONTHS'),
    message: t('activity:customers.FIRST_PAYMENT')
  };

  const arrData = {
    title: t('activity:customers.ARR'),
    contentText: generalData.arr
      ? typeof generalData.arr === 'string' &&
        (generalData.arr === 'na' || generalData.arr === 'n/a')
        ? 0
        : moneyFormaterFull(+generalData.arr.toFixed(0))
      : 0,
    arr: generalData.arrGraphic
  };

  return (
    <>
      <PageSubTitle hiden title="General Informaton" />
      <div className={classes.info}>
        {loaderGeneral ? (
          <Loading />
        ) : (
          <>
            <section className={classes.donutContainer}>
              <Donut
                size="xsmall"
                data={memberUtilizationData.data}
                title={memberUtilizationData.title}
                subtitle={memberUtilizationData.subtitle}
              />
            </section>
            <section className={classes.donutContainer}>
              <Donut
                size="xsmall"
                data={gatewaysUtilizationData.data}
                title={gatewaysUtilizationData.title}
                subtitle={gatewaysUtilizationData.subtitle}
              />
            </section>
            <section className={classes.donutContainer}>
              <Donut
                size="xsmall"
                data={mAppsUtilizationData.data}
                title={mAppsUtilizationData.title}
                subtitle={mAppsUtilizationData.subtitle}
              />
            </section>
            <Area areaChartData={arrData} />
            <TextChart data={billingCicle} />
            <TextChart data={loyalty} />
          </>
        )}
      </div>
      <div className={classes.general}>
        {show ? (
          <>
            <LineHS
              chartData={healthScoreData.yearHealthScores}
              linkTitle={
                healthScoreData.yearHealthScores
                  ? `${newUrl}/satisfaction/health-score`
                  : undefined
              }
            />
            <AreaBig chartData={activeUsersData} loader={loaderUsers} />
          </>
        ) : (
          <Loading />
        )}
      </div>
    </>
  );
};

General.propTypes = {
  customerId: PropTypes.string,
  status: PropTypes.string
};

export default General;
